import React, { useState, useEffect } from 'react';
import { Text, Flex } from '@chakra-ui/react';
import ReactECharts from 'echarts-for-react';
import ChartCard from './ChartCard';
import { analyticsService } from '../../services/analyticsService';
import type { LeaderboardEntry } from '../../services/analyticsService';

interface SopComplianceByRepChartProps {
  businessIdentifier?: string;
  timeRange?: string;
}

const FONT_FAMILY = 'TT Interphases Pro, Inter, sans-serif';

const getBarColor = (value: number): string => {
  if (value >= 80) return '#50C878';
  if (value >= 60) return '#F39C12';
  return '#FF6B6B';
};

const SopComplianceByRepChart: React.FC<SopComplianceByRepChartProps> = ({
  businessIdentifier,
  timeRange,
}) => {
  const [data, setData] = useState<LeaderboardEntry[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await analyticsService.getSalesLeaderboard(businessIdentifier, timeRange, 'avg_sop');
        const sorted = [...response.leaderboard].sort((a: LeaderboardEntry, b: LeaderboardEntry) => b.avg_sop_score - a.avg_sop_score);
        setData(sorted);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to fetch SOP compliance data';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [businessIdentifier, timeRange]);

  const getChartOption = () => {
    if (data.length === 0) return {};

    const names = data.map((entry) => entry.salesperson_name);
    const scores = data.map((entry) => entry.avg_sop_score);
    const colors = scores.map((score) => getBarColor(score));

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any) => {
          const p = params[0];
          return `${p.name}<br/>SOP Compliance: ${p.value.toFixed(1)}%`;
        },
        textStyle: {
          fontFamily: FONT_FAMILY,
        },
      },
      grid: {
        top: 30,
        right: 20,
        bottom: 60,
        left: 50,
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: names,
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          color: '#718096',
          rotate: names.length > 6 ? 30 : 0,
          interval: 0,
        },
        axisLine: {
          lineStyle: { color: '#E2E8F0' },
        },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 100,
        interval: 20,
        name: 'SOP Compliance %',
        nameTextStyle: {
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          color: '#A0AEC0',
        },
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          color: '#718096',
          formatter: '{value}%',
        },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: {
          lineStyle: {
            color: '#EDF2F7',
            type: 'dashed',
          },
        },
      },
      series: [
        {
          type: 'bar',
          data: scores.map((score, idx) => ({
            value: score,
            itemStyle: { color: colors[idx] },
          })),
          barMaxWidth: 40,
          label: {
            show: true,
            position: 'top',
            formatter: (params: any) => `${params.value.toFixed(1)}%`,
            fontFamily: FONT_FAMILY,
            fontSize: 11,
            color: '#4A5568',
          },
          itemStyle: {
            borderRadius: [4, 4, 0, 0],
          },
        },
      ],
    };
  };

  return (
    <ChartCard title="SOP Compliance Score by Rep" isLoading={isLoading} error={error}>
      {data.length > 0 ? (
        <ReactECharts
          option={getChartOption()}
          style={{ height: '300px', width: '100%' }}
          opts={{ renderer: 'svg' }}
        />
      ) : (
        <Flex align="center" justify="center" minH="200px">
          <Text color="gray.400" fontSize="sm" fontFamily={FONT_FAMILY}>
            No data available
          </Text>
        </Flex>
      )}
    </ChartCard>
  );
};

export default SopComplianceByRepChart;
